import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cashierApi } from '../api';
import {
  IconCalendar, IconCashier, IconChevronDown, IconDownload, IconEdit, IconInfo,
} from '../components/icons';
import {
  Empty, ErrorNote, Modal, Spinner, TH_LOCALE, dateOf, dayLabel, money, money2,
  timeOf, todayIso, useLoader,
} from '../components/common';
import { exportCsv } from '../components/exportCsv';

const cashierDay = (iso) =>
  dayLabel(iso, TH_LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * แคชเชียร์ — the drawer.
 *
 * The ledger merges cash taken against bills with manual petty cash, and shows
 * a running balance so the closing count can be reconciled line by line.
 */
export default function Cashier() {
  const [tab, setTab] = useState('current');
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [dialog, setDialog] = useState(null); // 'in' | 'out' | 'open' | 'close'
  const [historyFrom, setHistoryFrom] = useState(todayIso());
  const [historyTo, setHistoryTo] = useState(todayIso());

  const current = useLoader(() => cashierApi.current(), []);
  const history = useLoader(
    () => (tab === 'history'
      ? cashierApi.history(historyFrom, historyTo)
      : Promise.resolve(null)),
    [tab, historyFrom, historyTo],
  );

  if (current.loading) return <Spinner />;
  if (current.error) return <ErrorNote error={current.error} />;

  const { session, movements, summary } = current.data;

  const shown = (movements || []).filter((m) =>
    filter === 'all' ? true : filter === 'in' ? m.amount > 0 : m.amount < 0);

  return (
    <>
      <h1 className="cashier-title">แคชเชียร์</h1>
      <div className="cashier-topbar">
        <button className={`chip solid${tab === 'current' ? ' active' : ''}`} onClick={() => setTab('current')}>
          แคชเชียร์
        </button>
        <button className={`chip solid${tab === 'history' ? ' active' : ''}`} onClick={() => setTab('history')}>
          ประวัติแคชเชียร์
        </button>

        {tab === 'history' && (
          <>
            <label className="date-box cashier-date">
              <span>{cashierDay(historyFrom)}</span>
              <IconCalendar size={17} />
              <input
                type="date"
                value={historyFrom}
                max={historyTo}
                onChange={(event) => setHistoryFrom(event.target.value)}
                aria-label="วันที่เริ่มต้นประวัติแคชเชียร์"
              />
            </label>
            <span className="cashier-range-separator">-</span>
            <label className="date-box cashier-date">
              <span>{cashierDay(historyTo)}</span>
              <IconCalendar size={17} />
              <input
                type="date"
                value={historyTo}
                min={historyFrom}
                onChange={(event) => setHistoryTo(event.target.value)}
                aria-label="วันที่สิ้นสุดประวัติแคชเชียร์"
              />
            </label>
          </>
        )}

        <div className="cashier-topbar-spacer" />

        {tab === 'history' ? (
          <button
            className="btn-solid cashier-download"
            onClick={() => downloadCashierHistory(history.data?.sessions || [], historyFrom, historyTo)}
          >
            <IconDownload size={17} /> ดาวน์โหลดข้อมูล
          </button>
        ) : session ? (
          <>
            <span className="till-open">
              <IconCashier size={18} />
              เปิด {dateOf(session.openedAt)} {timeOf(session.openedAt)} รอบที่{session.roundNo}
            </span>
            <button
              className="btn cashier-close"
              onClick={() => setDialog('close')}
            >
              ปิดแคชเชียร์
            </button>
          </>
        ) : (
          <button className="btn btn-primary" onClick={() => setDialog('open')}>
            เปิดแคชเชียร์
          </button>
        )}
      </div>

      {editing && (
        <MovementEditor
          movement={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); current.reload(); }}
        />
      )}

      {tab === 'history' ? (
        <HistoryTable
          data={history.data}
          loading={history.loading}
          error={history.error}
          filter={filter}
          onFilter={setFilter}
        />
      ) : !session ? (
        <div className="card"><Empty icon="cashier" message="ยังไม่ได้เปิดแคชเชียร์วันนี้" /></div>
      ) : (
        <>
          <div className="stat-grid" style={{ marginBottom: 14 }}>
            <div className="stat-card">
              <div className="label">ยอดเงินสดปัจจุบัน</div>
              <div className="value" style={{ color: 'var(--purple-600)' }}>
                {money2(summary.currentCash)}
              </div>
              <div className="summary-row" style={{ marginTop: 12 }}>
                <span>ยอดเปิดแคชเชียร์</span>
                <strong>{money2(summary.openingFloat)}</strong>
              </div>
            </div>

            <div className="stat-card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className="label">นำเงินเข้า</div>
                <button className="btn btn-sm" onClick={() => setDialog('in')}>นำเงินเข้า</button>
              </div>
              <div className="value" style={{ color: 'var(--green-600)' }}>{money(summary.cashIn)}</div>
            </div>

            <div className="stat-card">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className="label">นำเงินออก</div>
                <button className="btn btn-sm" onClick={() => setDialog('out')}>นำเงินออก</button>
              </div>
              <div className="value" style={{ color: 'var(--red-600)' }}>{money(summary.cashOut)}</div>
            </div>
          </div>

          <div className="toolbar">
            {[['all', 'ทั้งหมด'], ['in', 'นำเงินเข้า'], ['out', 'นำเงินออก']].map(([k, label]) => (
              <button key={k} className={`chip${filter === k ? ' active' : ''}`} onClick={() => setFilter(k)}>
                {label}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <span className="till-meta">แคชเชียร์ : <b>{session.openedByName}</b></span>
            <span className="till-meta">รหัสแคชเชียร์ : <b>{session.sessionCode}</b></span>
          </div>

          <div className="card">
            <table className="data">
              <thead>
                <tr>
                  <th>เวลา</th>
                  <th>ประเภท</th>
                  <th>หมวดหมู่</th>
                  <th>แคชเชียร์</th>
                  <th>รายละเอียด</th>
                  <th className="num">จำนวน</th>
                  <th className="num">ราคารวม</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{dateOf(session.openedAt)} {timeOf(session.openedAt)} น.</td>
                  <td><span className="badge badge-grey">เปิด</span></td>
                  <td>เปิดแคชเชียร์</td>
                  <td>{session.openedByName || '-'}</td>
                  <td>-</td>
                  <td className="num">
                    {session.openingVariance == null ? '-' : (
                      <span style={{ color: session.openingVariance < 0 ? 'var(--red-600)' : 'var(--green-600)' }}>
                        {session.openingVariance > 0 ? '+' : ''}{money(session.openingVariance)}
                      </span>
                    )}
                  </td>
                  <td className="num" style={{ fontWeight: 700 }}>{money(summary.openingFloat)}</td>
                </tr>
                {shown.map((m) => (
                  <tr key={`${m.source}-${m.id}`}>
                    <td>{dateOf(m.occurredAt)} {timeOf(m.occurredAt)} น.</td>
                    <td>
                      <span className={`badge ${m.amount > 0 ? 'badge-green' : 'badge-red'}`}>
                        {m.amount > 0 ? 'นำเงินเข้า' : 'นำเงินออก'}
                      </span>
                    </td>
                    <td>{m.categoryName}</td>
                    <td>{m.cashierName || '-'}</td>
                    <td>
                      {m.detail || '-'}
                      {m.orderId && (
                        <button
                          className="row-info"
                          title="ดูรายละเอียดบิล"
                          onClick={() => navigate(`/history?order=${m.orderId}`)}
                        >
                          <IconInfo size={15} />
                        </button>
                      )}
                    </td>
                    <td className="num" style={{ color: m.amount > 0 ? 'var(--green-600)' : 'var(--red-600)' }}>
                      {m.amount > 0 ? '+' : ''}{money(m.amount)}
                    </td>
                    <td className="num" style={{ fontWeight: 700 }}>{money(m.runningBalance)}</td>
                    <td className="num">
                      {m.source === 'movement' && (
                        <button className="icon-square" title="แก้ไข" onClick={() => setEditing(m)}>
                          <IconEdit size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {dialog && (
        <CashDialog
          kind={dialog}
          expected={summary?.currentCash}
          session={session}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); current.reload(); }}
        />
      )}
    </>
  );
}

function CashDialog({ kind, expected, session, onClose, onDone }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Only the หมวดหมู่ that belong to this direction — 'ค่าน้ำค่าไฟ' is never
  // money coming in. Without a category the รายจ่าย breakdown on the report has
  // nothing to group by.
  const isMove = kind === 'in' || kind === 'out';
  const cats = useLoader(() => (isMove ? cashierApi.categories() : Promise.resolve(null)), [kind]);
  const options = (cats.data?.categories || []).filter((c) => c.direction === kind);

  const titles = {
    in: 'นำเงินเข้า', out: 'นำเงินออก',
    open: 'เปิดแคชเชียร์', close: 'ปิดแคชเชียร์',
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'open') await cashierApi.open(Number(amount) || 0);
      else if (kind === 'close') await cashierApi.close(Number(amount) || 0, note);
      else await cashierApi.movement({
        direction: kind,
        amount: Number(amount),
        note,
        categoryId: categoryId ? Number(categoryId) : undefined,
      });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={titles[kind]}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>ยกเลิก</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !amount}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      }
    >
      {error && <div className="login-error">{error}</div>}
      {kind === 'close' && session && (
        <div className="cash-dialog-period">
          วันที่ : {dateOf(session.openedAt)} รอบเปิดแคชเชียร์ : {session.roundNo}
        </div>
      )}
      {(isMove || kind === 'close') && (
        <div className="cash-dialog-balance">
          <span>จำนวนเงินในลิ้นชัก</span>
          <strong>{money(expected)}</strong>
        </div>
      )}
      {kind === 'close' && (
        <div className="summary-row">
          <span>ส่วนต่างของเงินสด</span>
          <strong className={Number(amount || 0) - Number(expected || 0) < 0 ? 'text-red' : 'text-green'}>
            {money(Number(amount || 0) - Number(expected || 0))}
          </strong>
        </div>
      )}
      {isMove && (
        <div className="field">
          <label>หมวดหมู่</label>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">เลือกหมวดหมู่</option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>{c.name_th || c.name_en}</option>
            ))}
          </select>
        </div>
      )}
      <div className="field">
        <label>
          {kind === 'open' ? 'ยอดเปิดแคชเชียร์'
            : kind === 'close' ? 'ยอดเงินสดปัจจุบัน'
            : kind === 'in' ? 'เพิ่มเงินเข้าลิ้นชัก เป็นจำนวน'
            : 'นำเงินออกจากลิ้นชัก เป็นจำนวน'}
        </label>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
      </div>
      {kind !== 'open' && (
        <div className="field">
          <div className="field-count">
            <label>รายละเอียด</label>
            <span className="counter">{note.length}/100</span>
          </div>
          <input
            value={note}
            maxLength={100}
            placeholder={kind === 'close' ? 'ใส่รายละเอียด' : 'รายละเอียด'}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}

function downloadCashierHistory(sessions, from, to) {
  const rows = sessions.flatMap((session) => {
    const opening = {
      sessionCode: session.sessionCode,
      occurredAt: session.openedAt,
      type: 'เปิด',
      categoryName: 'เปิดแคชเชียร์',
      cashierName: session.openedByName || '-',
      detail: '-',
      amount: 0,
      runningBalance: session.openingFloat,
    };
    const movements = (session.movements || []).map((movement) => ({
      sessionCode: session.sessionCode,
      occurredAt: movement.occurredAt,
      type: Number(movement.amount) >= 0 ? 'นำเงินเข้า' : 'นำเงินออก',
      categoryName: movement.categoryName,
      cashierName: movement.cashierName || '-',
      detail: movement.detail || '-',
      amount: movement.amount,
      runningBalance: movement.runningBalance,
    }));
    const closing = session.closedAt ? [{
      sessionCode: session.sessionCode,
      occurredAt: session.closedAt,
      type: 'ปิด',
      categoryName: 'ปิดแคชเชียร์',
      cashierName: session.closedByName || '-',
      detail: session.note || '-',
      amount: session.variance || 0,
      runningBalance: session.countedCash,
    }] : [];
    return [opening, ...movements, ...closing];
  });

  exportCsv(
    `cashier-history-${from}-${to}`,
    [
      { label: 'รหัสแคชเชียร์', value: (row) => row.sessionCode },
      { label: 'เวลา', value: (row) => `${dateOf(row.occurredAt)} ${timeOf(row.occurredAt)} น.` },
      { label: 'ประเภท', value: (row) => row.type },
      { label: 'หมวดหมู่', value: (row) => row.categoryName },
      { label: 'แคชเชียร์', value: (row) => row.cashierName },
      { label: 'รายละเอียด', value: (row) => row.detail },
      { label: 'จำนวน', value: (row) => row.amount },
      { label: 'ราคารวม', value: (row) => row.runningBalance },
    ],
    rows,
  );
}

function HistoryTable({ data, loading, error, filter, onFilter }) {
  const [selectedSession, setSelectedSession] = useState('all');
  const [collapsed, setCollapsed] = useState({});

  if (loading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const sessions = data?.sessions || [];
  const selectedExists = sessions.some((session) => String(session.id) === selectedSession);
  const visible = selectedSession === 'all' || !selectedExists
    ? sessions
    : sessions.filter((session) => String(session.id) === selectedSession);

  return (
    <>
      <div className="cashier-history-controls">
        <div className="cashier-history-tabs">
          {[['all', 'ทั้งหมด'], ['in', 'นำเงินเข้า'], ['out', 'นำเงินออก']].map(([key, label]) => (
            <button
              key={key}
              className={filter === key ? 'active' : ''}
              onClick={() => onFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <label className="cashier-session-filter">
          <span>รอบเปิดแคชเชียร์</span>
          <select value={selectedSession} onChange={(event) => setSelectedSession(event.target.value)}>
            <option value="all">ทั้งหมด</option>
            {sessions.map((session) => (
              <option value={session.id} key={session.id}>{session.sessionCode}</option>
            ))}
          </select>
          <IconChevronDown size={16} />
        </label>
      </div>

      {visible.length ? (
        <div className="cashier-history-list">
          {visible.map((session) => (
            <CashierHistorySession
              key={session.id}
              session={session}
              filter={filter}
              collapsed={Boolean(collapsed[session.id])}
              onToggle={() => setCollapsed((current) => ({
                ...current,
                [session.id]: !current[session.id],
              }))}
            />
          ))}
        </div>
      ) : (
        <div className="cashier-history-empty"><Empty icon="history" message="Data not found" /></div>
      )}
    </>
  );
}

function CashierHistorySession({ session, filter, collapsed, onToggle }) {
  const movements = (session.movements || []).filter((movement) =>
    filter === 'all'
      || (filter === 'in' && Number(movement.amount) > 0)
      || (filter === 'out' && Number(movement.amount) < 0));
  const showBookends = filter === 'all';
  const closingBalance = session.countedCash ?? session.summary?.currentCash ?? 0;

  return (
    <section className="cashier-history-session">
      <button className="cashier-history-summary" onClick={onToggle} type="button">
        <span><small>รหัสแคชเชียร์</small><strong>{session.sessionCode}</strong></span>
        <span><small>ผู้เปิด</small><strong>{session.openedByName || '-'}</strong></span>
        <span><small>เวลาเปิด</small><strong>{dateOf(session.openedAt)} {timeOf(session.openedAt)}</strong></span>
        <span><small>ผู้ปิด</small><strong>{session.closedByName || '-'}</strong></span>
        <span><small>เวลาปิด</small><strong>{session.closedAt ? `${dateOf(session.closedAt)} ${timeOf(session.closedAt)}` : '-'}</strong></span>
        <span className="num"><small>ยอดเปิด</small><strong>{money(session.openingFloat)}</strong></span>
        <span className="num"><small>ยอดปิด</small><strong>{money(closingBalance)}</strong></span>
        <IconChevronDown className={collapsed ? '' : 'flip'} size={17} />
      </button>

      {!collapsed && (
        <div className="cashier-history-ledger">
          <table>
            <thead>
              <tr>
                <th>เวลา</th>
                <th>ประเภท</th>
                <th>หมวดหมู่</th>
                <th>แคชเชียร์</th>
                <th>รายละเอียด</th>
                <th className="num">จำนวน</th>
                <th className="num">ราคารวม</th>
              </tr>
            </thead>
            <tbody>
              {showBookends && (
                <CashierHistoryRow
                  occurredAt={session.openedAt}
                  type="open"
                  category="เปิดแคชเชียร์"
                  cashier={session.openedByName}
                  detail="-"
                  amount={0}
                  balance={session.openingFloat}
                />
              )}
              {movements.map((movement) => (
                <CashierHistoryRow
                  key={`${movement.source}-${movement.id}`}
                  occurredAt={movement.occurredAt}
                  type={Number(movement.amount) >= 0 ? 'in' : 'out'}
                  category={movement.categoryName}
                  cashier={movement.cashierName}
                  detail={movement.detail}
                  amount={movement.amount}
                  balance={movement.runningBalance}
                />
              ))}
              {showBookends && session.closedAt && (
                <CashierHistoryRow
                  occurredAt={session.closedAt}
                  type="close"
                  category="ปิดแคชเชียร์"
                  cashier={session.closedByName}
                  detail={session.note || '-'}
                  amount={session.variance || 0}
                  balance={closingBalance}
                />
              )}
              {!showBookends && movements.length === 0 && (
                <tr><td colSpan="7" className="cashier-ledger-empty">ไม่พบรายการ</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CashierHistoryRow({
  occurredAt, type, category, cashier, detail, amount, balance,
}) {
  const labels = { open: 'เปิด', close: 'ปิด', in: 'นำเงินเข้า', out: 'นำเงินออก' };
  const numericAmount = Number(amount || 0);
  return (
    <tr>
      <td>{dateOf(occurredAt)} {timeOf(occurredAt)} น.</td>
      <td><span className={`cashier-kind ${type}`}>{labels[type]}</span></td>
      <td>{category || '-'}</td>
      <td>{cashier || '-'}</td>
      <td>{detail || '-'}</td>
      <td className={`num${numericAmount > 0 ? ' positive' : numericAmount < 0 ? ' negative' : ''}`}>
        {numericAmount > 0 ? '+' : ''}{money(numericAmount)}
      </td>
      <td className="num total">{money(balance)}</td>
    </tr>
  );
}

/** แก้ไขรายการเงินสด — only the movements typed in at the till. */
function MovementEditor({ movement, onClose, onSaved }) {
  const cats = useLoader(() => cashierApi.categories(), []);
  const [form, setForm] = useState({
    direction: movement.direction,
    amount: Math.abs(movement.amount),
    categoryId: '',
    note: movement.detail || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await cashierApi.updateMovement(movement.id, {
        direction: form.direction,
        amount: Number(form.amount),
        note: form.note,
        ...(form.categoryId ? { categoryId: Number(form.categoryId) } : {}),
      });
      onSaved();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Modal title="แก้ไขรายการ" onClose={onClose}>
      <ErrorNote error={error} />
      <label className="field">
        <span>ประเภท</span>
        <select value={form.direction} onChange={set('direction')}>
          <option value="in">นำเงินเข้า</option>
          <option value="out">นำเงินออก</option>
        </select>
      </label>
      <label className="field">
        <span>หมวดหมู่</span>
        <select value={form.categoryId} onChange={set('categoryId')}>
          <option value="">{movement.categoryName || 'ไม่เปลี่ยน'}</option>
          {(cats.data?.categories || []).map((c) => (
            <option key={c.id} value={c.id}>{c.name_th || c.name_en}</option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>จำนวนเงิน</span>
        <input type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} />
      </label>
      <label className="field">
        <span>รายละเอียด</span>
        <input value={form.note} onChange={set('note')} />
      </label>
      <div className="modal-actions">
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={onClose} disabled={busy}>ยกเลิก</button>
        <button className="btn btn-solid" onClick={save} disabled={busy}>
          {busy ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
      </div>
    </Modal>
  );
}
